import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import { IdentityLinksPanel } from "@/components/admin/IdentityLinksPanel";
import { IngressChainPanel } from "@/components/admin/IngressChainPanel";
import { toast } from "sonner";
import { LoadingState, ErrorState } from "@/components/shared/States";
import { SectionCard } from "@/components/shared/PageChrome";

// Non-secret readiness for the controlled staff pilot. Read-only: activation is an environment change made by operations.
export function PilotReadinessTab() {
  const [state, setState] = useState({ loading: true });
  const [viewer, setViewer] = useState(null);
  const openDoc = async (doc, download) => {
    try {
      const r = await api.get(`/admin/pilot-documents/${doc.key}`, { params: download ? { download: true } : {}, responseType: "blob" });
      if (download) {
        const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = doc.filename; a.click(); URL.revokeObjectURL(url);
      } else {
        setViewer({ filename: doc.filename, text: await r.data.text() });
      }
    } catch (e) { toast.error(e?.response?.status === 403 ? "Only a Jigsaw Admin can open pilot documents" : "Document not available"); }
  };
  useEffect(() => { api.get("/admin/pilot-readiness").then((r) => setState({ loading: false, data: r.data })).catch((e) => setState({ loading: false, error: e?.response?.status })); }, []);
  if (state.loading) return <LoadingState testId="pilot-readiness-loading" />;
  if (state.error === 403) return <p data-testid="pilot-readiness-restricted" className="rounded-md border border-jigsaw-border bg-white px-4 py-3 text-sm text-jigsaw-muted">Only a Jigsaw Admin can view pilot readiness.</p>;
  if (!state.data) return <ErrorState testId="pilot-readiness-error" />;
  const d = state.data;
  return (
    <div data-testid="pilot-readiness" className="space-y-4">
      <div data-testid="pilot-readiness-status" className={`flex items-start gap-3 rounded-[10px] border px-4 py-3 text-sm ${d.ready_for_named_testers ? "border-jigsaw-success/40 bg-jigsaw-successBg text-jigsaw-success" : "border-amber-300 bg-amber-50 text-amber-900"}`}>
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <p className="font-semibold">{d.ready_for_named_testers ? "Pilot may admit named testers" : d.pilot_mode ? "Pilot mode is on but blocked" : "Supervised synthetic mode - pilot not activated"}</p>
          <ul className="mt-1 list-disc pl-4 text-[13px]">{d.blocked_reasons.map((r) => <li key={r} data-testid="pilot-blocked-reason">{r}</li>)}</ul>
        </div>
      </div>
      <SectionCard title="Readiness checks" subtitle="Booleans only - no configuration values are ever shown here" testId="pilot-checks">
        <ul className="divide-y divide-jigsaw-border">
          {d.checks.map((c) => (
            <li key={c.key} data-testid={`pilot-check-${c.key}`} data-ok={c.ok} className="flex items-start gap-3 px-5 py-3 text-sm">
              {c.ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-jigsaw-success" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-jigsaw-danger" />}
              <div><p className="font-medium text-jigsaw-navy">{c.label}</p><p className="text-[12px] text-jigsaw-muted">{c.detail}</p></div>
            </li>
          ))}
        </ul>
      </SectionCard>
      <SectionCard title="Pilot-eligible users" subtitle="Set per user in the Users tab (explicit flag - shared demo personas stay excluded unless deliberately marked)" testId="pilot-eligible-users" bodyClassName="px-5 pb-4">
        {d.eligible_users.length === 0 ? <p className="text-[13px] text-jigsaw-muted">None yet.</p>
          : <ul className="text-[13px]">{d.eligible_users.map((u) => <li key={u.id} data-testid={`pilot-eligible-${u.id}`}>{u.full_name} · {u.email}</li>)}</ul>}
      </SectionCard>
      <IdentityLinksPanel staffUsers={d.staff_users} />
      <IngressChainPanel />

      <SectionCard title="Guide, scenarios and feedback" subtitle="Admin-only documents served by the platform (fixed list; nothing public)" testId="pilot-guides" bodyClassName="px-5 pb-4">
        <ul className="divide-y divide-jigsaw-border text-[13px]">
          {d.documents.map((doc) => (
            <li key={doc.key} data-testid={`pilot-guide-${doc.key}`} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <span><span className="font-medium text-jigsaw-navy">{doc.label}</span> <span className="font-mono-ref text-[11px] text-jigsaw-muted">{doc.filename}</span></span>
              <span className="flex gap-2">
                <button type="button" data-testid={`pilot-guide-view-${doc.key}`} className="text-[12px] text-[#1F4E9C] hover:underline" onClick={() => openDoc(doc, false)}>View</button>
                <button type="button" data-testid={`pilot-guide-download-${doc.key}`} className="text-[12px] text-[#1F4E9C] hover:underline" onClick={() => openDoc(doc, true)}>Download</button>
              </span>
            </li>
          ))}
        </ul>
        {viewer && (
          <div data-testid="pilot-guide-viewer" className="mt-3 rounded-md border border-jigsaw-border bg-[#FAFBFC] p-3">
            <div className="mb-2 flex items-center justify-between"><p className="text-[12px] font-semibold text-jigsaw-navy">{viewer.filename}</p><button type="button" data-testid="pilot-guide-viewer-close" className="text-[12px] text-jigsaw-muted hover:underline" onClick={() => setViewer(null)}>Close</button></div>
            <pre className="max-h-[420px] overflow-auto whitespace-pre-wrap text-[12px] leading-relaxed text-jigsaw-navy">{viewer.text}</pre>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
